import { SessionTutorialProvider } from '@/features/session-tutorial/SessionTutorialProvider';
import { TutorialAnchor } from '@/features/session-tutorial/TutorialAnchor';
import type { SessionTutorialSettings } from '@/features/session-tutorial/sessionTutorial.types';
import { cn } from '@/lib/utils';
import { FileText, Mic } from 'lucide-react';
import type { ReactNode } from 'react';

export type MobileSessionView = 'recording' | 'assistant';

interface SessionViewportProps {
  children: ReactNode;
  /** Shows the first-run session tips when the person has them pending. */
  tutorial?: SessionTutorialSettings;
}

export const SessionViewport = ({ children, tutorial }: SessionViewportProps) => {
  const viewport = (
    <div data-testid="session-viewport" className="flex h-dvh overflow-hidden bg-background md:h-screen">
      {children}
    </div>
  );
  if (!tutorial) return viewport;
  return <SessionTutorialProvider {...tutorial}>{viewport}</SessionTutorialProvider>;
};

interface MobileSessionNavigationProps {
  activeView: MobileSessionView;
  onViewChange: (view: MobileSessionView) => void;
}

export const MobileSessionNavigation = ({ activeView, onViewChange }: MobileSessionNavigationProps) => (
  <nav aria-label="Lotasýn" className="flex shrink-0 border-border border-t bg-white md:hidden">
    <button
      type="button"
      aria-current={activeView === 'recording' ? 'page' : undefined}
      onClick={() => onViewChange('recording')}
      className={cn(
        'flex flex-1 cursor-pointer flex-col items-center gap-1 py-3 text-xs transition-colors',
        activeView === 'recording' ? 'bg-primary/10 text-primary' : 'text-muted-foreground'
      )}
    >
      <Mic className="h-5 w-5" />
      <span>Upptaka</span>
    </button>
    <TutorialAnchor step="mobileAssistant" advanceOnClick>
      <button
        type="button"
        aria-current={activeView === 'assistant' ? 'page' : undefined}
        onClick={() => onViewChange('assistant')}
        className={cn(
          'flex flex-1 cursor-pointer flex-col items-center gap-1 py-3 text-xs transition-colors',
          activeView === 'assistant' ? 'bg-primary/10 text-primary' : 'text-muted-foreground'
        )}
      >
        <FileText className="h-5 w-5" />
        <span>Klínísk aðstoð</span>
      </button>
    </TutorialAnchor>
  </nav>
);

interface MobileAssistantShellProps {
  children: ReactNode;
  onViewChange: (view: MobileSessionView) => void;
}

export const MobileAssistantShell = ({ children, onViewChange }: MobileAssistantShellProps) => (
  <div
    data-testid="mobile-assistant-shell"
    className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-slate-50 md:hidden"
  >
    <div data-testid="mobile-assistant-content" className="min-h-0 min-w-0 flex-1 overflow-hidden">
      {children}
    </div>
    <MobileSessionNavigation activeView="assistant" onViewChange={onViewChange} />
  </div>
);
